import { FC, ReactNode } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Modal, Row, Typography } from 'antd';
import { TbCopy } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformAlert } from '@/shared/components/alerts';
import { toast } from '@/shared/components/toasts/PlatformToast';

export interface OneTimeCredentialValue {
    label: string;
    value: string;
}

export interface OneTimeCredentialDisclosure {
    copyLabel?: string;
    description?: ReactNode;
    title: ReactNode;
    values: OneTimeCredentialValue[];
    warningDescription?: ReactNode;
    warningTitle?: ReactNode;
}

export interface OneTimeCredentialModalProps {
    disclosure?: OneTimeCredentialDisclosure;
    onClose: () => void;
}

const OneTimeCredentialModal: FC<OneTimeCredentialModalProps> = ({ disclosure, onClose }) => {
    const { token } = useAntDesignTheme();

    // Unmounting immediately clears the one-time value from the DOM instead of
    // retaining it through the modal's exit animation.
    if (!disclosure) {
        return null;
    }

    const values = disclosure.values;
    const clipboardValue =
        values.length === 1 ? values[0]?.value : values.map(({ label, value }) => `${label}: ${value}`).join('\n');

    const copyValues = async () => {
        if (!clipboardValue) {
            return;
        }

        try {
            await navigator.clipboard.writeText(clipboardValue);
            toast(values.length === 1 ? `${values[0]?.label} Copied` : 'Credentials Copied');
        } catch (error) {
            captureException(error, { tags: { action: 'copy-one-time-credential' } });
            toast.error('Unable to copy credential');
        }
    };

    return (
        <Modal
            destroyOnHidden
            footer={
                <Row gutter={token.marginXS} wrap={false}>
                    <Col span={12}>
                        <Button block disabled={!clipboardValue} icon={<TbCopy size='1rem' />} onClick={copyValues}>
                            {disclosure.copyLabel ?? (values.length === 1 ? `Copy ${values[0]?.label}` : 'Copy Values')}
                        </Button>
                    </Col>
                    <Col span={12}>
                        <Button block onClick={onClose} type='primary'>
                            Done
                        </Button>
                    </Col>
                </Row>
            }
            keyboard={false}
            maskClosable={false}
            onCancel={onClose}
            open
            title={disclosure.title}
            width='min(34rem, calc(100vw - 2rem))'
        >
            {disclosure.description ? (
                <Typography.Paragraph style={{ color: token.colorTextSecondary, marginBottom: token.margin }}>
                    {disclosure.description}
                </Typography.Paragraph>
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
                {values.map(({ label, value }) => (
                    <div key={label}>
                        {values.length > 1 ? (
                            <small
                                style={{
                                    color: token.colorTextTertiary,
                                    display: 'block',
                                    marginBottom: token.marginXXS,
                                }}
                            >
                                {label}
                            </small>
                        ) : null}
                        <div
                            aria-label={`${label} value`}
                            style={{
                                background: token.colorFillQuaternary,
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: token.borderRadius,
                                boxSizing: 'border-box',
                                padding: `${token.paddingSM}px ${token.padding}px`,
                                width: '100%',
                            }}
                        >
                            <code style={{ overflowWrap: 'anywhere', userSelect: 'all' }}>{value}</code>
                        </div>
                    </div>
                ))}
            </div>

            <PlatformAlert
                description={
                    disclosure.warningDescription ??
                    'After you close this modal, the value is removed from the current session and cannot be recovered. Store it securely, such as in an environment variable or secrets manager.'
                }
                showIcon
                status='warning'
                styles={{ marginTop: token.margin }}
                title={disclosure.warningTitle ?? 'Save this credential before closing'}
            />
        </Modal>
    );
};

export default OneTimeCredentialModal;
